import { useFocusEffect, useRoute } from '@react-navigation/native';
import { useBandshopConfig } from 'context/BandshopConfigContex';
import { useBottomSheet } from 'context/BottomSheetContext';
import { useGlobalConfig } from 'context/GlobalContext';
import { useSocket } from 'context/SocketContext';
import { useMobileOrDesktop } from 'hoc/mobileOrDesktop';
import { useBandshopVisible } from 'hooks/useBandshopVisible';
import { useSelectedProfile } from 'hooks/useSelectedProfile';
import { RootStackScreenProps } from 'navigation/AppNavigator';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AppState,
  AppStateStatus,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { useAnalytics } from 'services/analytics/useAnalytics';
import { ProgramType } from 'utils/enum';
import { calcSizeForDevice, calcSizeForTv } from 'utils/index';
import { Platform } from 'utils/platform';
import { modifyVastUrl } from 'utils/vast';

import { heroStyle } from './ProgramScreen';

import Button from 'components/Button';
import NewGradientButton from 'components/Button/NewGradienteButton';
import useSuperiorNavbar from 'components/Header/helpers/useSuperiorNavbar';
import Hero from 'components/Hero';
import Player from 'components/Player';
import SpallaPlayerPlatform from 'components/Player/SpallaPlayer/SpallaPlayerPlatform';
import { usePlayer } from 'components/Player/context';
import ProgramContent from 'components/Program/Content';
import ProgramHeader from 'components/Program/Header';
import ProgramRelated from 'components/Program/Related';
import ProgramShared from 'components/Program/Shared';
import { useProgram } from 'components/Program/context';
import AccordionBandshop from 'components/Section/bandshop/AccordionBandshop';

const LiveScreen: React.FC<RootStackScreenProps<'Program'>> = ({ navigation }) => {
  const {
    isPlaying,
    isFullScreen,
    isStreaming,
    pause,
    play,
    setIsVisible,
    mute,
    setNextTrack,
    reset,
  } = usePlayer();

  const route = useRoute();
  const program = useProgram();
  const analytics = useAnalytics();
  const { ads, colors } = useGlobalConfig();
  const [isActive, setIsActive] = useState(false);
  const [mediaId, setMediaId] = useState<string>();
  const [appState, setAppState] = useState(AppState.currentState);
  const { setVisible, setLiveMenuItems } = useBottomSheet();
  const hasSuperiorNavbar = useSuperiorNavbar(route);
  const buttonRef = useRef<TouchableOpacity>(null);
  const isBandshopVisible = useBandshopVisible(ProgramType.LIVE, program);
  const bandshopConfig = useBandshopConfig();
  const genericCollectionId = bandshopConfig?.bandshopOffers?.collectionId;
  const collectionId = program?.bandshopOffers?.collectionId;
  const vtexId = collectionId ? collectionId : genericCollectionId;
  const { selectedProfile } = useSelectedProfile();
  const { watch } = useSocket();

  const [secondsWatched, setSecondsWatched] = useState<number>(0);
  const secondsRef = useRef<number>(0);
  const intervalRef = useRef<number | null>(null);
  const { width } = useWindowDimensions();
  const isMobile = Platform.OS === 'ios' || Platform.OS === 'android';
  const isUnder18 = selectedProfile?.ageRating !== '18';

  const getMarginTop = useMemo(() => {
    const marginTopForMobileWeb = Platform.isMobileWeb() ? 45 : 80;
    if (isFullScreen) return 0;
    return hasSuperiorNavbar ? marginTopForMobileWeb + 40 : marginTopForMobileWeb;
  }, [isFullScreen, hasSuperiorNavbar]);

  useEffect(() => {
    setNextTrack(undefined);
  }, []);

  useEffect(() => {
    // Inicia e para o timer
    if (isPlaying) {
      if (intervalRef.current == null) {
        intervalRef.current = setInterval(() => {
          // forma funcional garante estado correto; atualização do ref para leitura externa
          setSecondsWatched((prev) => {
            const next = prev + 1;
            secondsRef.current = next;
            return next;
          });
        }, 1000) as unknown as number;
      }
    } else {
      if (intervalRef.current != null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return navigation.addListener('beforeRemove', async () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      if (isPlaying && !isStreaming) pause();

      reset({ media: undefined });
      setVisible(false);
      mute(false);
      const timeStamp = new Date().toISOString();
      analytics.triggerLiveFinish({
        programId: program._id,
        programTitle: program.title,
        programAgeRating: program.rating,
        programAdUnit: program.ads?.adunit?.val!,
        programCategory: program.category.map((tag) => tag.name).join(', '),
        programGenre: program.genre.map((genre) => genre.name).join(', '),
        timeStamp: timeStamp,
        timeWatched: String(secondsRef?.current),
      });
    });
  }, [navigation, isPlaying, isStreaming, pause]);

  useFocusEffect(
    useCallback(() => {
      setIsActive(true);

      return () => {
        if (!isStreaming) pause();
        setIsActive(false);
      };
    }, [isStreaming])
  );

  useFocusEffect(
    useCallback(() => {
      const timeStamp = new Date().toISOString();

      analytics.triggerLiveScreenPageViewEvent({
        programId: program._id,
        programTitle: program.title,
        programAgeRating: program.rating,
        programAdUnit: program.ads?.adunit?.val!,
        programCategory: program.category.map((tag) => tag.name).join(', '),
        programGenre: program.genre.map((genre) => genre.name).join(', '),
        timeStamp: timeStamp,
      });
    }, [])
  );

  useEffect(() => {
    const subscription = AppState.addEventListener('change', (nextAppState: AppStateStatus) => {
      if (appState.match(/inactive|background/) && nextAppState === 'active') {
        if (Platform.isAndroidTV && buttonRef?.current) {
          buttonRef?.current?.requestTVFocus();
        }
      }
      setAppState(nextAppState);
    });

    return () => subscription.remove();
  }, [appState]);

  const playerProps: PlayerProps | undefined = useMemo(() => {
    const media: PlayerMedia = {
      title: program.title,
      image: [program.imageVertical?.url!, program.imageHorizontal?.url!],
      duration: program.duration,
      programId: program._id,
      programTitle: program.title,
      programUrl: program.url,
      ratingValue: program.rating,
      ratingDescription: program.rateDescription,
      videoTracks: [],
      mediaSource: program.spallaID || program.mediaSource,
    };

    const mediaIdDub = program.mediaIdDub;
    const mediaIdDubSpalla = program.videoIdDub;

    if (mediaIdDub) {
      media.videoTracks.push({
        language: 'dub',
        trackId: media.mediaSource ? mediaIdDubSpalla : mediaIdDub,
      });
    }

    media.videoTracks.push({
      language: 'origin',
      trackId: media.mediaSource ? program.spallaID || program.videoId : program.mediaId,
    });

    return {
      media,
      isLive: true,
      adTagUrl:
        ads?.disableAds || program.ads?.disableAds || isUnder18
          ? undefined
          : Platform.isWebTV
          ? ads?.ctvVast
          : modifyVastUrl(ads?.vast, program),
      adsExpirationTime: ads?.adsExpirationTime,
    };
  }, [program, mediaId]);

  useEffect(() => {
    if (program?.ads?.disable_autoPlay !== true) {
      setMediaId(program.mediaId || program.spallaID || program.videoId);
    }

    setLiveMenuItems?.([]); //TO DO: The data is inside "program.liveMenu"
  }, [program]);

  useEffect(() => {
    if (isActive && playerProps?.media) {
      watch(playerProps).catch(() => undefined);
    }
  }, [isActive, playerProps?.media?.mediaId, playerProps?.media?.programId]);

  const Video = useMemo(
    () =>
      mediaId !== undefined && isActive && playerProps !== undefined ? (
        <Player {...playerProps} autoplay={!Platform.isWebTV} />
      ) : undefined,
    [mediaId, isActive, playerProps]
  );

  const [VideoMobile, VideoDesktop] = useMobileOrDesktop(Video, Video);

  useEffect(() => {
    if (program?.ads?.disable_autoPlay === true || !mediaId) {
      setMediaId(program.mediaId || program.spallaID || program.videoId);
    }
  }, [program, mediaId]);

  return (
    <>
      {VideoMobile !== undefined && Platform.OS !== 'web' && (
        <View style={{ marginTop: getMarginTop }}>{Video}</View>
      )}
      {(Platform.OS === 'web' || !isFullScreen) && (
        <Hero program={program} hideCover={mediaId !== undefined}>
          {VideoMobile !== undefined && Platform.OS === 'web' && (
            <SpallaPlayerPlatform streamId={program?.spallaID} autoplay={true} isLive />
          )}

          <View style={heroStyle.container}>
            <Hero.Header
              program={program}
              mediaId={mediaId}
              Aside={
                VideoDesktop !== undefined && !Platform.isAndroidTV ? (
                  <View style={{ width: Platform.isTV ? 0 : '80%', overflow: 'hidden' }}>
                    {Platform.isWebTV ? (
                      <>{Video}</>
                    ) : (
                      <SpallaPlayerPlatform streamId={program.spallaID} autoplay={true} isLive />
                    )}
                  </View>
                ) : undefined
              }>
              <ProgramHeader />
              {Platform.isTV && (
                <View style={{ marginTop: calcSizeForTv(30) }}>
                  <Button
                    ref={buttonRef}
                    useTVFocus
                    hasTVPreferredFocus
                    style={{ width: calcSizeForTv(300), height: calcSizeForTv(52) }}
                    textStyle={{ fontSize: calcSizeForTv(24) }}
                    iconStyle={{ width: calcSizeForTv(30), height: calcSizeForTv(30) }}
                    iconType="Play"
                    variant="gradient"
                    gradientVariant={colors ? 'newBtnColors' : undefined}
                    size="md"
                    onPress={() => {
                      play();
                      setIsVisible(true);
                    }}>
                    Assistir
                  </Button>
                </View>
              )}
              {program?.ads?.disable_autoPlay && !Platform.isTV && (
                <View
                  style={{
                    width: '100%',
                    alignItems: isMobile || width < 500 ? 'center' : 'flex-start',
                    justifyContent: 'center',
                  }}>
                  <NewGradientButton
                    width={calcSizeForDevice(350, 400)}
                    height={calcSizeForDevice(40, 52)}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: 'auto',
                    }}
                    display={mediaId && !Platform.isTV ? 'none' : undefined}
                    onPress={() => {
                      play();
                    }}>
                    <Text
                      style={{
                        fontWeight: 'bold',
                        color: '#070d31',
                        textTransform: 'uppercase',
                        fontSize: calcSizeForTv(16),
                      }}>
                      Assistir
                    </Text>
                  </NewGradientButton>
                </View>
              )}

              <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 10, gap: 28 }}>
                <ProgramShared />
              </View>
              <ProgramContent />
            </Hero.Header>

            {isBandshopVisible && vtexId && (
              <View style={{ paddingVertical: calcSizeForDevice(24) }}>
                <AccordionBandshop collectionId={vtexId} />
              </View>
            )}

            <ProgramRelated />
          </View>
        </Hero>
      )}
    </>
  );
};

export default LiveScreen;
